/**
 * RiskGauge — Animated SVG semicircle gauge displaying a 0-100 risk score.
 *
 * Features:
 *   - 180-degree arc with a three-zone gradient (green / yellow / red)
 *   - Smooth fill animation on mount (1.5 s ease-out via Framer Motion)
 *   - Large numeric readout centered in the arc
 *   - Dynamic glow that shifts color based on the active zone
 *   - Tick marks at 0, 25, 50, 75, 100
 *   - Responsive sizing via the `size` prop
 *
 * Props:
 *   value     number   — current risk score
 *   maxValue  number   — scale ceiling (default 100)
 *   label     string   — descriptor shown below the number
 *   size      number   — outer diameter in px (default 200)
 */
import { useMemo } from 'react'
import { motion, useMotionValue, useTransform, animate } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'

/* ================================================================
   DESIGN TOKENS
   ================================================================ */
const ZONE_LOW = '#34d399'     // signal-low  (green)
const ZONE_MED = '#ffb020'     // signal-medium (amber)
const ZONE_HIGH = '#ff2d55'    // signal-critical (red)

const SURFACE_2 = '#111425'
const EDGE = '#1e2240'
const TEXT_PRIMARY = '#e8eaf0'
const TEXT_SECONDARY = '#8b90a8'
const TEXT_TERTIARY = '#5a5f78'
const ACCENT_GHOST = 'rgba(0, 200, 255, 0.08)'

/* ================================================================
   HELPERS
   ================================================================ */

/** Map a normalised 0-1 ratio to a color across three zones. */
function zoneColor(ratio) {
  if (ratio <= 0.4) return ZONE_LOW
  if (ratio <= 0.7) return ZONE_MED
  return ZONE_HIGH
}

/** Interpolate between two hex colors at t [0-1]. */
function lerpColor(a, b, t) {
  const parse = c => [
    parseInt(c.slice(1, 3), 16),
    parseInt(c.slice(3, 5), 16),
    parseInt(c.slice(5, 7), 16),
  ]
  const ca = parse(a)
  const cb = parse(b)
  const r = Math.round(ca[0] + (cb[0] - ca[0]) * t)
  const g = Math.round(ca[1] + (cb[1] - ca[1]) * t)
  const bl = Math.round(ca[2] + (cb[2] - ca[2]) * t)
  return `#${[r, g, bl].map(v => v.toString(16).padStart(2, '0')).join('')}`
}

/** Smooth zone color with interpolation across boundaries. */
function smoothColor(ratio) {
  if (ratio <= 0.35) return ZONE_LOW
  if (ratio <= 0.45) return lerpColor(ZONE_LOW, ZONE_MED, (ratio - 0.35) / 0.1)
  if (ratio <= 0.65) return ZONE_MED
  if (ratio <= 0.75) return lerpColor(ZONE_MED, ZONE_HIGH, (ratio - 0.65) / 0.1)
  return ZONE_HIGH
}

/** Describe an SVG arc from startAngle to endAngle (radians) on a circle. */
function describeArc(cx, cy, r, startAngle, endAngle) {
  const x1 = cx + r * Math.cos(startAngle)
  const y1 = cy + r * Math.sin(startAngle)
  const x2 = cx + r * Math.cos(endAngle)
  const y2 = cy + r * Math.sin(endAngle)
  const largeArc = endAngle - startAngle > Math.PI ? 1 : 0
  return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`
}

/* ================================================================
   ANIMATED NUMBER — Counts up smoothly
   ================================================================ */
function AnimatedNumber({ value, duration = 1.5 }) {
  const [display, setDisplay] = useState(0)
  const mv = useMotionValue(0)

  useEffect(() => {
    const unsub = mv.on('change', v => setDisplay(Math.round(v)))
    const controls = animate(mv, value, {
      duration,
      ease: [0.16, 1, 0.3, 1],
    })
    return () => { controls.stop(); unsub() }
  }, [value, duration, mv])

  return <>{display}</>
}

/* ================================================================
   TICK MARKS
   ================================================================ */
function Ticks({ cx, cy, r, maxValue }) {
  const ticks = [0, 25, 50, 75, 100].map(v => ({
    val: v,
    ratio: v / maxValue,
  }))

  return (
    <g>
      {ticks.map(({ val, ratio }) => {
        const angle = Math.PI + ratio * Math.PI
        const outerR = r + 4
        const innerR = r - 6
        const labelR = r + 16
        const x1 = cx + outerR * Math.cos(angle)
        const y1 = cy + outerR * Math.sin(angle)
        const x2 = cx + innerR * Math.cos(angle)
        const y2 = cy + innerR * Math.sin(angle)
        const lx = cx + labelR * Math.cos(angle)
        const ly = cy + labelR * Math.sin(angle)

        return (
          <g key={val}>
            <line
              x1={x1} y1={y1} x2={x2} y2={y2}
              stroke={TEXT_TERTIARY}
              strokeWidth="1.2"
              strokeLinecap="round"
            />
            <text
              x={lx} y={ly}
              textAnchor="middle"
              dominantBaseline="middle"
              fill={TEXT_TERTIARY}
              fontSize="9"
              fontFamily="'DM Sans', sans-serif"
              fontWeight="500"
            >
              {val}
            </text>
          </g>
        )
      })}
    </g>
  )
}

/* ================================================================
   MAIN COMPONENT
   ================================================================ */
export default function RiskGauge({
  value = 0,
  maxValue = 100,
  label = 'Risk Score',
  size = 200,
}) {
  const clamped = Math.max(0, Math.min(value, maxValue))
  const ratio = clamped / maxValue
  const activeColor = smoothColor(ratio)

  // Arc geometry (semicircle opens upward ─ from PI to 2*PI)
  const strokeW = size * 0.08
  const r = (size - strokeW) / 2 - 20        // radius accounting for stroke + tick labels
  const cx = size / 2
  const cy = size / 2 + 10                    // push center down a bit so arc sits nicely

  const startAngle = Math.PI                  // left (180 deg)
  const endAngle = 2 * Math.PI               // right (360 deg)
  const arcLength = r * Math.PI               // half circumference

  // Full background arc path
  const bgArc = describeArc(cx, cy, r, startAngle, endAngle)

  // Gradient stops — mapped to the arc via linearGradient left-to-right
  const gradId = useMemo(() => `rg-grad-${Math.random().toString(36).slice(2, 8)}`, [])
  const glowId = useMemo(() => `rg-glow-${Math.random().toString(36).slice(2, 8)}`, [])

  return (
    <motion.div
      className="inline-flex flex-col items-center"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
    >
      <svg
        width={size}
        height={size * 0.62}
        viewBox={`0 0 ${size} ${size * 0.62}`}
        overflow="visible"
      >
        <defs>
          {/* Three-zone gradient across the arc (left to right = 0 to 100) */}
          <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={ZONE_LOW} />
            <stop offset="38%" stopColor={ZONE_LOW} />
            <stop offset="50%" stopColor={ZONE_MED} />
            <stop offset="68%" stopColor={ZONE_MED} />
            <stop offset="82%" stopColor={ZONE_HIGH} />
            <stop offset="100%" stopColor={ZONE_HIGH} />
          </linearGradient>

          {/* Glow filter that adopts the active zone color */}
          <filter id={glowId} x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="5" result="blur" />
            <feFlood floodColor={activeColor} floodOpacity="0.35" result="col" />
            <feComposite in="col" in2="blur" operator="in" result="gc" />
            <feMerge>
              <feMergeNode in="gc" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Background track */}
        <path
          d={bgArc}
          fill="none"
          stroke={EDGE}
          strokeWidth={strokeW}
          strokeLinecap="round"
          opacity="0.5"
        />

        {/* Foreground (animated) arc */}
        <motion.path
          d={bgArc}
          fill="none"
          stroke={`url(#${gradId})`}
          strokeWidth={strokeW}
          strokeLinecap="round"
          filter={`url(#${glowId})`}
          strokeDasharray={arcLength}
          initial={{ strokeDashoffset: arcLength }}
          animate={{ strokeDashoffset: arcLength * (1 - ratio) }}
          transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1] }}
        />

        {/* Needle endpoint dot */}
        <motion.circle
          r={strokeW * 0.4}
          fill={activeColor}
          style={{
            filter: `drop-shadow(0 0 4px ${activeColor})`,
          }}
          initial={{ cx: cx + r * Math.cos(startAngle), cy: cy + r * Math.sin(startAngle) }}
          animate={{
            cx: cx + r * Math.cos(startAngle + ratio * Math.PI),
            cy: cy + r * Math.sin(startAngle + ratio * Math.PI),
          }}
          transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1] }}
        />

        {/* Tick marks */}
        <Ticks cx={cx} cy={cy} r={r} maxValue={maxValue} />

        {/* Center value */}
        <text
          x={cx}
          y={cy - 6}
          textAnchor="middle"
          dominantBaseline="middle"
          fill={TEXT_PRIMARY}
          fontSize={size * 0.2}
          fontFamily="'Instrument Sans', 'DM Sans', sans-serif"
          fontWeight="700"
          style={{ filter: `drop-shadow(0 0 8px ${activeColor}40)` }}
        >
          <AnimatedNumber value={clamped} />
        </text>

        {/* Label */}
        <text
          x={cx}
          y={cy + size * 0.1}
          textAnchor="middle"
          fill={TEXT_SECONDARY}
          fontSize="11"
          fontFamily="'DM Sans', sans-serif"
          fontWeight="500"
          letterSpacing="0.04em"
        >
          {label}
        </text>
      </svg>

      {/* Severity zone bar beneath the gauge */}
      <div
        className="flex items-center gap-3 mt-1"
        style={{ opacity: 0.7 }}
      >
        {[
          { color: ZONE_LOW, label: 'Low' },
          { color: ZONE_MED, label: 'Medium' },
          { color: ZONE_HIGH, label: 'Critical' },
        ].map(z => (
          <div key={z.label} className="flex items-center gap-1">
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{ backgroundColor: z.color }}
            />
            <span
              className="text-[9px] font-medium uppercase tracking-wider"
              style={{ color: TEXT_TERTIARY }}
            >
              {z.label}
            </span>
          </div>
        ))}
      </div>
    </motion.div>
  )
}
